// ============================================================
// 15. TopSellingProductsPage.tsx
// ============================================================
// src/features/reports/pages/TopSellingProductsPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getTopSellingProducts, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    TrendingUp, Package, DollarSign, ShoppingCart,
    BarChart3, Zap, Star, Award
} from 'lucide-react';
import { motion } from 'framer-motion';

export function TopSellingProductsPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-top-selling', tenantId, params],
        queryFn: () => getTopSellingProducts(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatNumber = (val: string | number) => parseFloat(String(val || '0')).toLocaleString();

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'top_selling', format, params as any);
        window.open(url, '_blank');
    };

    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    const products = data?.products || [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <ReportHeader
                title="Top Selling Products"
                description="Best performing products"
                icon={TrendingUp}
                iconGradient="from-emerald-500 to-teal-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data || products.length === 0 ? (
                    <EmptyState
                        icon={<TrendingUp className="w-10 h-10" />}
                        title="No Sales Data"
                        description="No product sales found for the selected period."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <SummaryCard
                                label="Total Products"
                                value={String(data.summary?.totalProducts || 0)}
                                icon={Package}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <SummaryCard
                                label="Total Revenue"
                                value={formatNaira(data.summary?.totalRevenue || 0)}
                                icon={DollarSign}
                                gradient="from-blue-500 to-indigo-600"
                            />
                            <SummaryCard
                                label="Total Profit"
                                value={formatNaira(data.summary?.totalProfit || 0)}
                                icon={TrendingUp}
                                gradient="from-purple-500 to-violet-600"
                            />
                            <SummaryCard
                                label="Avg Margin"
                                value={`${(parseFloat(data.summary?.totalProfit || '0') / parseFloat(data.summary?.totalRevenue || '1') * 100).toFixed(1)}%`}
                                icon={BarChart3}
                                gradient="from-amber-500 to-orange-600"
                            />
                        </div>

                        {/* Top Products Bar Chart */}
                        <Card>
                            <CardContent className="p-5">
                                <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                    <Zap className="w-4 h-4 text-amber-500" />
                                    Sales Performance
                                </h3>
                                <div className="space-y-3">
                                    {products.slice(0, 15).map((product: any, i: number) => {
                                        const maxQty = products[0]?.quantitySold || 1;
                                        const percentage = (product.quantitySold / maxQty * 100);

                                        return (
                                            <div key={i} className="flex items-center gap-3">
                                                <div className="flex items-center gap-2 w-48">
                                                    <span className={`text-xs font-bold w-6 ${i < 3 ? 'text-amber-600' : 'text-slate-400'
                                                        }`}>
                                                        {i + 1}
                                                    </span>
                                                    <span className="text-xs text-slate-700 truncate">{product.name}</span>
                                                    {i < 3 && <Star className="w-3 h-3 text-amber-400 flex-shrink-0" />}
                                                </div>
                                                <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                                                    <motion.div
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${percentage}%` }}
                                                        transition={{ duration: 0.5, delay: i * 0.03 }}
                                                        className={`h-full rounded-full ${i === 0 ? 'bg-gradient-to-r from-amber-400 to-yellow-500' :
                                                                i === 1 ? 'bg-gradient-to-r from-slate-400 to-slate-500' :
                                                                    i === 2 ? 'bg-gradient-to-r from-orange-400 to-amber-500' :
                                                                        'bg-gradient-to-r from-emerald-400 to-teal-500'
                                                            }`}
                                                    />
                                                </div>
                                                <span className="text-xs font-bold text-slate-700 w-20 text-right">
                                                    {formatNumber(product.quantitySold)} sold
                                                </span>
                                                <span className="text-xs text-slate-500 w-24 text-right">
                                                    {formatNaira(product.revenue)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase w-12">#</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Product</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Quantity Sold</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Revenue</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Profit</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Margin</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Transactions</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Avg Qty/Sale</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {products.map((product: any, i: number) => (
                                            <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <span className={`text-sm font-bold ${i < 3 ? 'text-amber-600' : 'text-slate-400'
                                                        }`}>
                                                        {i + 1}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${i === 0 ? 'bg-amber-100' :
                                                                i === 1 ? 'bg-slate-100' :
                                                                    i === 2 ? 'bg-orange-100' :
                                                                        'bg-emerald-100'
                                                            }`}>
                                                            {i < 3 ? (
                                                                <Award className={`w-4 h-4 ${i === 0 ? 'text-amber-600' :
                                                                        i === 1 ? 'text-slate-600' :
                                                                            'text-orange-600'
                                                                    }`} />
                                                            ) : (
                                                                <Package className="w-4 h-4 text-emerald-600" />
                                                            )}
                                                        </div>
                                                        <span className="text-sm font-medium text-slate-900">{product.name}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm font-semibold">
                                                    {formatNumber(product.quantitySold)}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm font-bold text-slate-900">
                                                    {formatNaira(product.revenue)}
                                                </td>
                                                <td className="px-6 py-4 text-right text-sm font-bold">
                                                    <span className={parseFloat(product.profit) >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                                                        {formatNaira(product.profit)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <Badge
                                                        variant={parseFloat(product.margin) > 30 ? 'success' : parseFloat(product.margin) > 10 ? 'warning' : 'danger'}
                                                        size="sm"
                                                    >
                                                        {product.margin}%
                                                    </Badge>
                                                </td>
                                                <td className="px-6 py-4 text-center text-sm text-slate-500">
                                                    {product.transactions}
                                                </td>
                                                <td className="px-6 py-4 text-center text-sm text-slate-500">
                                                    {(product.quantitySold / (product.transactions || 1)).toFixed(1)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}